import { submitAdvertiseForm, TSubmitAdvertiseFormRequest } from "api/clinicos";
import {
  FormNameType,
  ProcedureType,
  TFormName,
  UploadTypeType,
} from "api/type";
import { ClinicContext, SessionContext } from "contexts";
import React, { useContext } from "react";
import PhoneInput from "react-phone-input-2";
import { callFormSubmissionTracking } from "utils/callClinicosTracking";
import { useUserLocation } from "utils/customHook";
import { generateUUID } from "utils/generateUUID";
import { mixpanelTrack } from "utils/mixpanel";
import NormalText from "./NormalText";
import PrimaryButton from "./PrimaryButton";

type PatientInfoFormProps = {
  batchId: string;
  uploadType: UploadTypeType;
  handleSubmit: () => void;
  selectedProcedure: ProcedureType | null;
};

const PatientInfoForm: React.FC<PatientInfoFormProps> = ({
  batchId,
  uploadType,
  selectedProcedure,
  handleSubmit,
}) => {
  const session = useContext(SessionContext);
  const fetchUserLocation = useUserLocation(session);
  const clinic = useContext(ClinicContext);

  const [firstName, setFirstName] = React.useState<string>("");
  const [lastName, setLastName] = React.useState<string>("");
  const [phone, setPhone] = React.useState<string>("");
  const [error, setError] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isFormSubmitted, setIsFormSubmitted] = React.useState<boolean>(false);

  const handleOnPressSubmit = async () => {
    if (!session) {
      return;
    }

    if (firstName.trim().length < 2 || firstName.trim().length > 20) {
      setError("First name must be between 2 and 20 characters.");
      return;
    }
    if (lastName.trim().length < 2 || lastName.trim().length > 20) {
      setError("Last name must be between 2 and 20 characters.");
      return;
    }

    try {
      setLoading(true);
      setIsFormSubmitted(true);
      await fetchUserLocation();
      const body: TSubmitAdvertiseFormRequest = {
        ...(batchId ? { batch_id: batchId } : {}),
        form_user_id: generateUUID(),
        form_name: FormNameType.ONBOARDING_TIER1,
        is_debug: process.env.REACT_APP_ENV === "production" ? false : true,
        form_data: { name: firstName, phone: phone },
        patient_record: {
          first_name: firstName,
          last_name: lastName,
          ...(selectedProcedure
            ? { procedures_of_interest: [selectedProcedure] }
            : {}),
          phone: phone,
          location: await fetchUserLocation(),
          upload_type: uploadType,
        },
      };
      await submitAdvertiseForm(session, body);
      mixpanelTrack("User Details Captured", [
        {
          propertyName: "Clinic Name",
          propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
        },
      ]);
      mixpanelTrack("Form Submitted", [
        {
          propertyName: "Clinic Name",
          propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
        },
      ]);
      callFormSubmissionTracking(session, TFormName.ONBOARDING_TIER1);
      handleSubmit();
    } catch (error) {
      setError("Failed to submit form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        gap: 10,
        width: "75%",
        display: "flex",
        borderRadius: 20,
        marginLeft: "auto",
        padding: "10px 20px",
        flexDirection: "column",
        border: "1px solid #C4C6CD",
      }}
    >
      <NormalText
        textContent="How can we reach you?"
        style={{ fontWeight: "bold" }}
      />
      <div>
        <div style={{ display: "flex" }}>
          <NormalText textContent="First Name" />
        </div>
        <input
          disabled={isFormSubmitted}
          type="text"
          onChange={(e) => setFirstName(e.target.value)}
          required
          autoFocus
          style={{ fontSize: "16px", padding: "0 10px", ...styles.input }}
        />
      </div>
      <div>
        <div style={{ display: "flex" }}>
          <NormalText textContent="Last Name" />
        </div>
        <input
          disabled={isFormSubmitted}
          type="text"
          onChange={(e) => setLastName(e.target.value)}
          required
          style={{ fontSize: "16px", padding: "0 10px", ...styles.input }}
        />
      </div>
      <div>
        <div style={{ display: "flex" }}>
          <NormalText textContent="Phone" />
        </div>
        <PhoneInput
          disabled={isFormSubmitted}
          containerStyle={{ fontSize: "16px" }}
          inputStyle={styles.input}
          country={"us"}
          onlyCountries={["us"]}
          showDropdown={false}
          onChange={setPhone}
          inputProps={{
            required: true,
          }}
        />
      </div>
      {error && <NormalText textContent={error} style={styles.errorText} />}
      <PrimaryButton
        text={"See my results"}
        type="button"
        fullWidth
        loading={loading}
        disabled={
          !firstName || !lastName || !(phone.length > 10) || isFormSubmitted
        }
        useThemeColor={false}
        displayPoweredBy={false}
        onClick={handleOnPressSubmit}
      />
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  input: {
    fontWeight: 500,
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #CACACA",
    height: "35px",
    borderRadius: "3px",
  },
  errorText: {
    color: "red",
    fontSize: "14px",
  },
} as const;

export default PatientInfoForm;
